import { Injectable } from '@angular/core';
import { Room } from '../../models/room.model';
import { BaseService, IKeyValuePair } from './base.service';
import { Observable } from 'rxjs';
import { Occupancy } from '../../models/occupancy';
import { DateHelper } from '../../utils/date-helper';
import { AvailableRoom } from '../../models/available-room';
import { Constants } from '../../models/constants.model';
import { StatusId } from '../../models/status-id';
import { HttpClient } from '@angular/common/http';
import { AuthorizationService } from '../authorization.service';
import { MessageService } from '../message.service';
import { CacheService } from '../cache.service';
import { LanguageService } from '../language.service';
import { TranslateService } from '@ngx-translate/core';
import { ResourceTableRow } from '../../models/resource-table-row';
import { AppConfigService } from '../app-config.service';

@Injectable({
  providedIn: 'root'
})
export class RoomService extends BaseService<Room>  {
  private static fieldDefinitions = [
    {
      columnName: 'Building',
      captionLocaleKey: 'BUILDING',
      columnSpan: 1,
      showFromScreenSize: undefined
    },
    {
      columnName: 'Floor',
      captionLocaleKey: 'FLOOR',
      columnSpan: 1,
      showFromScreenSize: undefined
    },
    {
      columnName: 'NumberPersons',
      captionLocaleKey: 'NUMBER_PERSONS',
      columnSpan: 1,
      showFromScreenSize: undefined
    },
    {
      columnName: 'Room',
      captionLocaleKey: 'ROOMNUMBER',
      columnSpan: 1,
      showFromScreenSize: undefined
    },
    {
      columnName: 'RoomType',
      captionLocaleKey: 'ROOM_TYPE',
      columnSpan: 1,
      showFromScreenSize: undefined
    }
  ];

  protected endpointUri = 'RoomReservation/Rooms';

  public static selectVisibleFields(ar: AvailableRoom): ResourceTableRow[] {
    const reqFields = ['Column', 'Size'];

    if (Array.isArray(AppConfigService.settings.RoomColumns) !== true) {
      throw new Error('Missing `RoomColumns` in the app configuration');
    }

    const fieldsSelection = AppConfigService.settings.RoomColumns;
    const fsValid = fieldsSelection.every(fsel => reqFields.every(f => f in fsel));

    if (fsValid !== true) { throw new Error('Some field definitions are invalid format'); }

    const visibleFiSe = fieldsSelection.reduce((displayFields, fs) => {
      // lookup field def for user defined field selection
      const fieldDef = RoomService.fieldDefinitions.find(fdef => fdef.columnName === fs.Column);
      if (fieldDef) {
        // update field defs parameters
        fieldDef.showFromScreenSize = fs.Size;

        const tableRow = ResourceTableRow.create(fieldDef, ar !== null ? ar[fieldDef.columnName] : null);
        displayFields.push(tableRow);
      }
      return displayFields;

    }, new Array<ResourceTableRow>());

    return visibleFiSe;
  }

  constructor(
    http: HttpClient,
    authorization: AuthorizationService,
    messages: MessageService,
    cache: CacheService,
    private language: LanguageService,
    private translation: TranslateService) {
    super(http, authorization, messages, cache);
  }

  /**
   * Returns the first warning if the AvailableRoom ca not be reserved
   *
   * @param {AvailableRoom} room
   * @param {Date} searchDate
   * @returns {string}
   * @memberof RoomService
   */
  public getFirstWarning(room: AvailableRoom, searchDate: Date): Observable<string> {
    // status
    switch (room.StatusId) {
      case StatusId.rrGesperrt:
        return this.translation.get('ROOM_STATE_CLOSED');
      case StatusId.rrGesperrtBis:
        if (room.StatusDate > searchDate) {
          return this.translation.get('ROOM_STATE_CLOSED_UNTIL', { date: this.language.formatDate(room.StatusDate) });
        }
        break;
      case StatusId.rrReservierbarBis:
        if (room.StatusDate < searchDate) {
          return this.translation.get('ROOM_STATE_OPEN_UNTIL', { date: this.language.formatDate(room.StatusDate) });
        }
        break;
      case StatusId.rrReservierbar: // ok
        break;
      default:
        console.warn(`status unknown: (${room.StatusId} - ${room.Status})`);
        break;
    }

    // c)	hat das Attribut «Belegung erlaubt» inaktiviert:
    if (!room.StatusAllowOccupation) {
      return this.translation.get('ROOM_STATE_CLOSED');
    }

    // reservationstyp
    switch (room.ReservationRightId) {
      case Constants.ReservationsRecht.Reservable:
        // ok
        break;
      case Constants.ReservationsRecht.ProvReservable:
        return this.translation.get('ROOM_PERMISSION_PROV');
      case Constants.ReservationsRecht.Requestable:
        return this.translation.get('ROOM_PERMISSION_REQUEST');
      case Constants.ReservationsRecht.Visible:
      case Constants.ReservationsRecht.None:
        return this.translation.get('ROOM_NO_PERMISSION_OCCUPY');
      default:
        console.log(room);
        throw Error('invalid reservation type: ' + room.ReservationRightId);
    }

    // keine Arbeitszeit
    if (!room.IsWorkingDay) {
      return this.translation.get('ROOM_STATE_NO_WORKDAY', { date: this.language.formatDate(searchDate) });
    }

    return null;
  }

  getAll(): Observable<Room[]> {
    throw new Error('Method not implemented.');
  }

  get(id: string): Observable<any> {
    return this.getRequest(id);
  }

  add(entity: Room): Observable<any> {
    throw new Error('Method not implemented.');
  }
  update(entity: Room): Observable<any> {
    throw new Error('Method not implemented.');
  }
  delete(id: string): Observable<{}> {
    throw new Error('Method not implemented.');
  }

  getRoomDropDownData(buildingIds: Array<string>): Observable<any> {
      return this.getRequest(null, 'DropDownItems/RoomDropDownData', [{ key: 'buildings', value: buildingIds.join(',') }]);
  }

  getAvailableRooms(params: IAvailableRoomsParams): Observable<any> {
    return this.getRequest(null, this.endpointUri + '/Available', this.mapAvailableRoomsParams(params));
  }

  /**
   * Map search-params from IAvailableRoomsParams
   *
   * @private
   * @param {IAvailableRoomsParams} params
   * @returns {IKeyValuePair[]}
   * @memberof RoomService
   */
  private mapAvailableRoomsParams(params: IAvailableRoomsParams): IKeyValuePair<string, string>[] {
    return [
      { key: nameof<IAvailableRoomsParams>('availableFrom'), value: String(DateHelper.getUnixTime(params.availableFrom)) },
      { key: nameof<IAvailableRoomsParams>('availableTo'), value: String(DateHelper.getUnixTime(params.availableTo)) },
      { key: nameof<IAvailableRoomsParams>('availableOnly'), value: params.availableOnly ? '1' : null },
      { key: nameof<IAvailableRoomsParams>('buildings'), value: params.buildings.join(',') },
      { key: nameof<IAvailableRoomsParams>('equipments'), value: params.equipments.join(',') },
      { key: nameof<IAvailableRoomsParams>('floors'), value: params.floors.join(',') },
      { key: nameof<IAvailableRoomsParams>('locations'), value: params.locations.join(',') },
      { key: 'designations', value: params.ressourceNumber.join(',') },
      { key: nameof<IAvailableRoomsParams>('idRoom'), value: params.idRoom.join(',') },
      { key: nameof<IAvailableRoomsParams>('roomTypes'), value: params.roomTypes.join(',') },
      { key: nameof<IAvailableRoomsParams>('seats'), value: params.seats.join(',') },
      { key: nameof<IAvailableRoomsParams>('timespan'), value: params.timespan ? String(params.timespan * 60) : '' }
    ];
  }

  getOccupanciesById(roomId: number, dateFrom: Date, dateTo: Date): Observable<any> {
    return this.getRequest(null, this.endpointUri + '/' + String(roomId) + '/Occupancies', [
      { key: 'dateFrom', value: String(DateHelper.getUnixTime(dateFrom)) },
      { key: 'dateTo', value: String(DateHelper.getUnixTime(dateTo)) }
    ]);
  }

  public mapOccupancies(data: any[]): Occupancy[] {
    const raw = data as Occupancy[];
    return raw.map(x => Occupancy.create(x));
  }
}

export interface IAvailableRessourceParams {

  ressourceNumber: string[];

  /**
     * available from datetime
     *
     * @type {Date}
     * @memberof IAvailableRoomsParams
     */
  availableFrom: Date;

  /**
   * available to datetime
   *
   * @type {Date}
   * @memberof IAvailableRoomsParams
   */
  availableTo: Date;

  availableOnly: boolean;

  timespan: number;
}

/**
 * Paramter object for getAvailableRooms
 *
 * @export
 * @interface IAvailableRoomsParams
 */
export interface IAvailableRoomsParams extends IAvailableRessourceParams {

  idRoom: number[];

  buildings: number[];

  equipments: string[];

  floors: string[];

  locations: number[];

  roomTypes: number[];

  seats: string[];

}
